/**
 * UsersManager Component
 *
 * Manages users: list, add, edit, delete.
 * Role-based behavior:
 * - super_admin: Sees all users, can create any role
 * - admin: Sees only viewers in their client, can only create viewers
 */
import { useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';
import { useAuth } from '../../contexts/AuthContext';
import { DataTable, ClientFilter, ConfirmDialog, type Column } from '../ui';
import { UserForm } from './UserForm';
import { ViewerPermissionsModal } from './ViewerPermissionsModal';
import type { User, UserFormData, UserRole } from '../../types';

const roleLabels: Record<UserRole, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  viewer: 'Viewer',
};

const roleBadgeColors: Record<UserRole, string> = {
  super_admin: 'bg-red-100 text-red-800',
  admin: 'bg-purple-100 text-purple-800',
  viewer: 'bg-gray-100 text-gray-800',
};

export const UsersManager = () => {
  const {
    clients,
    isLoading,
    selectedClientId,
    setSelectedClientId,
    filteredUsers,
    createUser,
    updateUser,
    deleteUser,
    getClientById,
  } = useAdmin();

  const { user: currentUser, isSuperAdmin, isAdmin, client: currentClient, startImpersonation } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter users based on role
  // super_admin: sees all users
  // admin: sees only viewers in their client
  const displayedUsers = useMemo(() => {
    if (isSuperAdmin) {
      return filteredUsers;
    }
    // Admin: show only viewers from their own client
    return filteredUsers.filter(
      (u) => u.role === 'viewer' && u.clientId === currentClient?.id
    );
  }, [filteredUsers, isSuperAdmin, currentClient]);

  // Allowed roles for creating users
  const allowedRoles: UserRole[] = useMemo(() => {
    if (isSuperAdmin) {
      return ['super_admin', 'admin', 'viewer'];
    }
    // Admin can only create viewers
    return ['viewer'];
  }, [isSuperAdmin]);

  // Open form for adding
  const handleAdd = () => {
    setSelectedUser(null);
    setIsFormOpen(true);
  };

  // Open form for editing
  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setIsFormOpen(true);
  };

  // Open delete confirmation
  const handleDeleteClick = (user: User) => {
    setSelectedUser(user);
    setIsDeleteDialogOpen(true);
  };

  // Submit form (create or update)
  const handleFormSubmit = useCallback(
    async (data: UserFormData) => {
      setIsSubmitting(true);
      try {
        if (selectedUser) {
          await updateUser(selectedUser.id, data);
        } else {
          await createUser(data);
        }
        setIsFormOpen(false);
      } finally {
        setIsSubmitting(false);
      }
    },
    [selectedUser, createUser, updateUser]
  );

  // Confirm delete
  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedUser) return;

    setIsSubmitting(true);
    try {
      await deleteUser(selectedUser.id);
      setIsDeleteDialogOpen(false);
      setSelectedUser(null);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedUser, deleteUser]);

  // Check if user can be deleted (not self)
  const canDeleteUser = (user: User) => {
    return user.id !== currentUser?.id;
  };

  // Check if current user can impersonate target user
  const canImpersonate = (user: User) => {
    // Cannot impersonate self
    if (user.id === currentUser?.id) return false;

    if (isSuperAdmin) {
      // Super admin can impersonate any non-super-admin user
      return user.role !== 'super_admin';
    }

    if (isAdmin) {
      // Admin can only impersonate viewers in their own client
      return user.role === 'viewer' && user.clientId === currentClient?.id;
    }

    return false;
  };

  // Handle impersonation
  const handleImpersonate = (user: User) => {
    const userClient = user.clientId ? getClientById(user.clientId) : null;
    // Save current path to return to after stopping impersonation
    startImpersonation(user, userClient || null, location.pathname);
    // Navigate to home to see the impersonated view
    navigate('/');
  };

  // Open permissions modal for a viewer
  const handleManagePermissions = (user: User) => {
    setSelectedUser(user);
    setIsPermissionsModalOpen(true);
  };

  // Table columns
  const columns: Column<User>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (user) => (
        <div>
          <div className="font-medium text-gray-900">
            {user.firstName} {user.lastName}
          </div>
          {user.id === currentUser?.id && (
            <span className="text-xs text-gray-500">(You)</span>
          )}
        </div>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      render: (user) => (
        <span className="text-gray-600">{user.email}</span>
      ),
    },
    {
      key: 'phone',
      header: 'Phone',
      render: (user) => (
        <span className="text-gray-600">{user.phone || '-'}</span>
      ),
    },
    {
      key: 'clientId',
      header: 'Client',
      render: (user) => {
        const client = user.clientId ? getClientById(user.clientId) : null;
        return (
          <div className="flex items-center gap-2">
            {client && (
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: client.color }}
              />
            )}
            <span>{client?.name || (user.role === 'super_admin' ? 'All Clients' : '-')}</span>
          </div>
        );
      },
    },
    {
      key: 'role',
      header: 'Role',
      width: '120px',
      render: (user) => (
        <span
          className={`
            inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
            ${roleBadgeColors[user.role]}
          `}
        >
          {roleLabels[user.role]}
        </span>
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">
          {isSuperAdmin ? 'Users' : 'Viewers'}
        </h3>
        <div className="flex items-center gap-4">
          {/* Client filter - only for super_admin */}
          {isSuperAdmin && (
            <ClientFilter
              clients={clients}
              selectedClientId={selectedClientId}
              onChange={setSelectedClientId}
              loading={isLoading}
            />
          )}
          <button
            onClick={handleAdd}
            className="px-4 py-2 text-sm font-medium text-white bg-metrica-green rounded-lg hover:bg-metrica-green/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-metrica-green"
          >
            + Add {isSuperAdmin ? 'User' : 'Viewer'}
          </button>
        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={displayedUsers}
        loading={isLoading}
        keyExtractor={(u) => u.id}
        emptyMessage={
          isSuperAdmin
            ? 'No users yet. Add your first user to get started.'
            : 'No viewers yet. Add your first viewer to get started.'
        }
        actions={(user) => (
          <>
            {/* View as (Impersonate) */}
            {canImpersonate(user) && (
              <button
                onClick={() => handleImpersonate(user)}
                className="p-1 text-gray-400 hover:text-amber-600 transition-colors"
                title="View as this user"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
            )}
            {/* Manage Permissions - only for viewers */}
            {user.role === 'viewer' && (
              <button
                onClick={() => handleManagePermissions(user)}
                className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                title="Manage Permissions"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </button>
            )}
            <button
              onClick={() => handleEdit(user)}
              className="p-1 text-gray-400 hover:text-metrica-green transition-colors"
              title="Edit"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              onClick={() => handleDeleteClick(user)}
              disabled={!canDeleteUser(user)}
              className="p-1 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={canDeleteUser(user) ? 'Delete' : 'Cannot delete yourself'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </>
        )}
      />

      {/* Add/Edit Form */}
      <UserForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleFormSubmit}
        user={selectedUser}
        clients={clients}
        selectedClientId={isSuperAdmin ? selectedClientId : currentClient?.id}
        isLoading={isSubmitting}
        allowedRoles={allowedRoles}
        showClientSelect={isSuperAdmin}
      />

      {/* Viewer Permissions Modal */}
      <ViewerPermissionsModal
        isOpen={isPermissionsModalOpen}
        onClose={() => setIsPermissionsModalOpen(false)}
        viewer={selectedUser}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={`Delete ${isSuperAdmin ? 'User' : 'Viewer'}`}
        message={`Are you sure you want to delete "${selectedUser?.firstName} ${selectedUser?.lastName}"? This action cannot be undone.`}
        confirmText="Delete"
        type="danger"
        isLoading={isSubmitting}
      />
    </div>
  );
};

export default UsersManager;
